export const bloggerTags = ["Bloggers"] as const;

export interface BloggerUser {
    id: number;
    secret_cookie: string | null;
    name: string | null;
    surname: string | null;
    telegram_username: string | null;
}

export interface Blogger {
    id: number;
    display_name: string | null;
    user: BloggerUser;
    created_at: string | null;
}

export interface BloggerListItem extends Blogger {
    direct: BloggerMetricSet;
}

export interface BloggerListResponse {
    success: boolean;
    data: BloggerListItem[];
    totals: BloggerMetricSet;
}

export interface BloggerMutationResponse {
    success: boolean;
    data: Blogger;
}

export interface BloggerMetricSet {
    registered: number;
    ever_paid: number;
    current_active: number;
}

export interface BloggerLevelMetric extends BloggerMetricSet {
    level: number;
}

export interface BloggerStatsResponse {
    success: boolean;
    data: {
        blogger: {
            id: number;
            user_id: number;
            secret_cookie: string | null;
            name: string | null;
            surname: string | null;
            telegram_username: string | null;
            display_name: string | null;
        };
        registration_period: {
            date_from: string | null;
            date_to: string | null;
            timezone: "UTC";
        };
        direct: BloggerMetricSet;
        invitation_tree: BloggerMetricSet;
        entire_structure: BloggerMetricSet;
        structure_by_level: BloggerLevelMetric[];
    };
}

export interface BloggerStatsParams {
    bloggerId: number;
    date_from?: string;
    date_to?: string;
}

export interface UpdateBloggerParams {
    bloggerId: number;
    display_name: string;
}

export interface BloggerInvitationNode {
    id: number;
    inviter_id: number;
    name: string | null;
    surname: string | null;
    telegram_username: string | null;
    secret_cookie: string | null;
    created_at: string | null;
    ever_paid: boolean;
    current_active: boolean;
    children_count: number;
}

export interface BloggerInvitationChildrenParams {
    bloggerId: number;
    parentUserId?: number;
    page?: number;
    perPage?: number;
}

export interface BloggerInvitationChildrenResponse {
    success: boolean;
    data: BloggerInvitationNode[];
    meta: {
        parent_user_id: number;
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
    };
}

export interface BloggerChartStat {
    id: number;
    display_name: string | null;
    user: {
        id: number;
        name: string | null;
        surname: string | null;
        telegram_username: string | null;
    };
    direct_invited_count: number;
    total_invited_count: number;
}

export interface BloggerChartStatsResponse {
    success: boolean;
    data: BloggerChartStat[];
}

export const bloggerEndpoints = {
    getBloggers: {
        query: () => ({
            url: "/admin/bloggers",
        }),
        providesTags: bloggerTags,
    },
    addBlogger: {
        query: (userIdentifier: string) => ({
            url: "/admin/bloggers",
            method: "POST",
            body: {
                user_identifier: userIdentifier,
            },
        }),
        invalidatesTags: bloggerTags,
    },
    removeBlogger: {
        query: (bloggerId: number) => ({
            url: `/admin/bloggers/${bloggerId}`,
            method: "DELETE",
        }),
        invalidatesTags: bloggerTags,
    },
    updateBlogger: {
        query: ({ bloggerId, display_name }: UpdateBloggerParams) => ({
            url: `/admin/bloggers/${bloggerId}`,
            method: "PATCH",
            body: {
                display_name,
            },
        }),
        invalidatesTags: bloggerTags,
    },
    getBloggerStats: {
        query: ({ bloggerId, ...params }: BloggerStatsParams) => ({
            url: `/admin/bloggers/${bloggerId}/stats`,
            params,
        }),
    },
    getBloggerInvitationChildren: {
        query: ({
            bloggerId,
            parentUserId,
            page = 1,
            perPage = 50,
        }: BloggerInvitationChildrenParams) => ({
            url: `/admin/bloggers/${bloggerId}/invitation-children`,
            params: {
                ...(parentUserId !== undefined
                    ? { parent_user_id: parentUserId }
                    : {}),
                page,
                per_page: perPage,
            },
        }),
    },
    getBloggerChartStats: {
        query: () => ({
            url: "/admin/bloggers/chart-stats",
        }),
        providesTags: bloggerTags,
    },
};
